import axios from "axios";
import {
  buildAuthHeaders,
  getCallCenterApiErrorMessage,
  getCallCenterBaseUrl,
  requestCallCenter,
  unwrapEnvelopeValue,
} from "./CallCenterApi";
import {
  ensureSuperAdminOrganizationContext,
  getSelectedOrganizationId,
  isPlatformSuperAdmin,
} from "../utils/organizationContext";
import { cachedRequest, invalidateCachedRequest } from "./callCenterRequestCache";

const DASHBOARD_CACHE_TTL_MS = 3 * 60 * 1000;

export const getDashboardAnalytics = async ({ startDate, endDate, forceRefresh = false } = {}) => {
  const cacheKey = `dashboard:${startDate || "all"}:${endDate || "all"}`;
  const fetchDashboard = async () => {
    const envelope = await requestCallCenter({
      method: "get",
      url: "callcenter/analytics/dashboard",
      params: {
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(forceRefresh ? { refresh: true } : {}),
      },
    });
    return unwrapEnvelopeValue(envelope, "Failed to load dashboard analytics");
  };

  if (forceRefresh) {
    invalidateCachedRequest(cacheKey);
    return fetchDashboard();
  }

  return cachedRequest(cacheKey, fetchDashboard, DASHBOARD_CACHE_TTL_MS);
};

export const listWeeklyTrackerAgents = async () => {
  const envelope = await requestCallCenter({
    method: "get",
    url: "callcenter/analytics/agents",
  });
  return unwrapEnvelopeValue(envelope, "Failed to load call agents");
};

const buildWeeklyTrackerPayload = ({ weekStart, agentIds, totalAgentCount }) => {
  const payload = {};
  if (weekStart) payload.weekStart = weekStart;
  if (Array.isArray(agentIds) && agentIds.length) {
    const normalizedIds = agentIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    const allSelected = totalAgentCount > 0 && normalizedIds.length >= totalAgentCount;
    if (!allSelected && normalizedIds.length) {
      payload.agentIds = normalizedIds;
    }
  }
  return payload;
};

export const getWeeklyTracker = async ({ weekStart, agentIds, totalAgentCount } = {}) => {
  const payload = buildWeeklyTrackerPayload({ weekStart, agentIds, totalAgentCount });
  try {
    const envelope = await requestCallCenter({
      method: "post",
      url: "callcenter/analytics/weekly-tracker",
      data: payload,
    });
    return unwrapEnvelopeValue(envelope, "Failed to load weekly tracker");
  } catch (error) {
    const status = error?.httpStatus ?? error?.response?.status;
    if (status === 404 || status === 405) {
      const envelope = await requestCallCenter({
        method: "get",
        url: "callcenter/analytics/weekly-tracker",
        params: {
          ...(weekStart ? { weekStart } : {}),
          ...(payload.agentIds?.length ? { agentIds: payload.agentIds } : {}),
        },
      });
      return unwrapEnvelopeValue(envelope, "Failed to load weekly tracker");
    }
    throw error;
  }
};

const saveWeeklyTrackerExportBlob = async (response) => {
  const blob = response.data;
  const contentType = response.headers?.["content-type"] || "";
  if (contentType.includes("application/json") || blob?.type?.includes("application/json")) {
    const text = await blob.text();
    let message = "Failed to export weekly tracker";
    try {
      message = getCallCenterApiErrorMessage(JSON.parse(text), message);
    } catch {
      // keep default message
    }
    throw new Error(message);
  }
  const disposition = response.headers["content-disposition"] || "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] || "Call_Center_Weekly_Tracker.xlsx";
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
  return filename;
};

export const downloadWeeklyTrackerExcel = async ({ weekStart, agentIds, totalAgentCount } = {}) => {
  if (isPlatformSuperAdmin() && !getSelectedOrganizationId()) {
    await ensureSuperAdminOrganizationContext();
  }

  const payload = buildWeeklyTrackerPayload({ weekStart, agentIds, totalAgentCount });
  const exportUrl = `${getCallCenterBaseUrl()}api/callcenter/analytics/weekly-tracker/export`;

  try {
    const response = await axios.post(exportUrl, payload, {
      responseType: "blob",
      headers: buildAuthHeaders({ "Content-Type": "application/json" }),
    });
    return await saveWeeklyTrackerExportBlob(response);
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404 || status === 405) {
      const response = await axios.get(exportUrl, {
        responseType: "blob",
        headers: buildAuthHeaders(),
        params: {
          ...(weekStart ? { weekStart } : {}),
          ...(payload.agentIds?.length ? { agentIds: payload.agentIds } : {}),
        },
      });
      return await saveWeeklyTrackerExportBlob(response);
    }
    const errorBlob = error?.response?.data;
    if (errorBlob instanceof Blob) {
      const text = await errorBlob.text();
      let message = "Failed to export weekly tracker";
      try {
        message = getCallCenterApiErrorMessage(JSON.parse(text), message);
      } catch {
        // keep default message
      }
      throw new Error(message);
    }
    throw error;
  }
};
